import React from 'react';
import { Field, FieldValidator } from 'formik';
import { useTranslation } from 'react-i18next';
import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';

import useClasses from '../../../hooks/useClasses';
import Button from '../../Button';

import './style.scss';

// Compatible with RDC FormikWrapper
interface Props {
  title: string
  fieldName: string
  validation?: FieldValidator
  hint?: string
  stepIncrement?: number
  value?: string
  min?: number
  max?: number
  unitName?: string
  resetText?: string
}

const StepperField: React.FC<Props> = ({
  fieldName,
  title,
  stepIncrement = 1,
  hint,
  min = 0,
  max,
  unitName,
  resetText,
  validation,
}) => {
  const { buildClass } = useClasses('Stepper');
  const { t } = useTranslation();

  const formik = useExtendedFormikContext();
  const error = formik.errors[fieldName];
  const touched = formik.touched[fieldName];

  function minimumGuard(newValue: string) {
    if (newValue == null || newValue === '') {
      // eslint-disable-next-line no-void
      void formik.setFieldValue(fieldName, '');
    }

    const parsed = parseFloat(newValue);

    if (Number.isNaN(parsed) || parsed < min) {
      // don't commit a change if not a valid number
      return;
    }

    // eslint-disable-next-line no-void
    void formik.setFieldValue(fieldName, parsed);
  }

  function validate(value: string) {
    if (validation) {
      return validation(value);
    }

    if (min) {
      const parsed = parseFloat(value);

      if (Number.isNaN(parsed) || parsed < min) {
        return t('webform.components.number.min', {
          min,
        });
      }
    }

    if (max) {
      const parsed = parseFloat(value);

      if (Number.isNaN(parsed) || parsed > max) {
        return t('webform.components.number.max', {
          max,
        });
      }
    }

    return undefined;
  }

  return (
    <div className={buildClass()}>
      <div className={buildClass('container')}>

        <label htmlFor={fieldName} className={buildClass('label')}>
          {title}
        </label>

        {/* TODO: add error styling when design is available */}
        {error && touched && (
          <span className="FormElement__error">{error}</span>
        )}

        {hint && typeof hint === 'string' && (
          <span className={buildClass('hint')}>
            {hint}
          </span>
        )}

        <Field
          name={fieldName}
          validate={validate}
        >{() => (
          <StepperInput
            fieldName={fieldName}
            value={formik.values[fieldName]}
            unitName={unitName}
            stepIncrement={stepIncrement}
            min={min}
            max={max}
            resetText={resetText}
            onChange={(newValue) => formik.setFieldValue(fieldName, newValue)}
            onBlur={(newValue) => minimumGuard(newValue)}
          />
        )}
        </Field>
      </div>
    </div>
  );
};

interface StepperInputProps {
  fieldName: string
  value: string
  resetText?: string
  unitName?: string
  stepIncrement: number
  min?: number
  max?: number
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
}

// TODO: use new icons for the increment and decrement signs
function StepperInput({
  fieldName,
  value,
  resetText,
  unitName,
  stepIncrement = 1,
  min = 0,
  max,
  onChange,
  onBlur,
}: StepperInputProps) {
  const { buildClass, joinClasses } = useClasses('Stepper');
  const { t } = useTranslation();

  const increment = React.useCallback(() => {
    const oldValue = Number.isNaN(value)
      ? min
      : Number(value);

    const incrementedValue = oldValue + stepIncrement;

    const newValue = max
      ? Math.min(max, incrementedValue).toString()
      : incrementedValue.toString();

    onChange(newValue);
  }, [max, min, onChange, stepIncrement, value]);

  const decrement = React.useCallback(() => {
    const oldValue = Number.isNaN(value)
      ? min
      : Number(value);

    const decrementedValue = oldValue - stepIncrement;

    const newValue = Math.max(min, decrementedValue).toString();

    onChange(newValue);
  }, [value, min, stepIncrement, onChange]);

  function reset() {
    onChange('0');
  }

  const inputRef = React.useRef<HTMLInputElement>();

  const decrementRef = React.useRef<HTMLButtonElement>();

  const incrementRef = React.useRef<HTMLButtonElement>();

  React.useEffect(() => {
    function inputRefOnKeyDown(e: KeyboardEvent) {
      if (!inputRef.current) {
        return;
      }

      if (e.key === 'ArrowUp') {
        increment();
      }

      if (e.key === 'ArrowDown') {
        decrement();
      }

      if (e.key === 'ArrowLeft' && inputRef.current.selectionStart === 0) {
        decrementRef.current?.focus();
      }

      if (e.key === 'ArrowRight' && inputRef.current.selectionStart === inputRef.current.value.length) {
        incrementRef.current?.focus();
      }
    }

    function decrementRefOnKeydown(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') {
        focusOnInputRef();
      }
    }

    function incrementRefOnKeydown(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        focusOnInputRef();
      }
    }

    function focusOnInputRef() {
      inputRef.current.focus();

      const { length } = inputRef.current.value;

      // wait until focus is done
      setTimeout(() => {
        inputRef.current.setSelectionRange(length, length);
      }, 0);
    }

    inputRef.current?.addEventListener('keydown', inputRefOnKeyDown);
    decrementRef.current?.addEventListener('keydown', decrementRefOnKeydown);
    incrementRef.current?.addEventListener('keydown', incrementRefOnKeydown);

    return () => {
      inputRef.current?.removeEventListener('keydown', inputRefOnKeyDown);
      decrementRef.current?.removeEventListener('keydown', decrementRefOnKeydown);
      incrementRef.current?.removeEventListener('keydown', incrementRefOnKeydown);
    };
  }, [increment, decrement]);

  return (
    <>
      <div
        className={buildClass('controls')}
        role="spinbutton"
        aria-valuenow={!Number.isNaN(value) && Number(value)}
        aria-valuemin={0}
      >
        <button
          type="button"
          onClick={decrement}
          className={joinClasses(buildClass('control', 'decrement'), 'icon-after')}
          tabIndex={-1}
          ref={decrementRef}
        >
          <span className="sr-only">{t('pages.daily_registration.buttons.decrement')}</span>
        </button>

        <div className={buildClass('input-container')}>
          <input
            type="text"
            ref={inputRef}
            className={buildClass('input', {
              'with-unit-name': unitName,
            })}
            autoComplete="off"
            inputMode="decimal"
            id={fieldName}
            name={fieldName}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => onBlur && onBlur(e.target.value)}
            value={value}
          />

          {unitName && (
            <span className={buildClass('unit-name')}>
              {unitName}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={increment}
          className={joinClasses(buildClass('control', 'increment'), 'icon-after')}
          tabIndex={-1}
          ref={incrementRef}
        >
          <span className="sr-only">{t('pages.daily_registration.buttons.increment')}</span>
        </button>
      </div>

      {resetText && (
        <div className={buildClass('reset-button-container')}>
          <Button type="button" secondary className={buildClass('reset-button')} onClick={reset}>{resetText}</Button>
        </div>
      )}
    </>
  );
}

export default StepperField;
